import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCcw, Loader2, CheckCircle, AlertTriangle,
  TrendingDown, TrendingUp, User, Calendar, Search, Download,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { formatAdminError } from "../lib/adminError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Money({ v, cls = "" }) {
  if (v === null || v === undefined) return <span className="text-slate-500">—</span>;
  const n = typeof v === "number" ? v : parseFloat(v);
  if (Number.isNaN(n)) return <span className="text-slate-500">—</span>;
  return <span className={cls}>${n.toFixed(2)}</span>;
}

export default function AdminFeeReconciliation() {
  const [loading, setLoading] = useState(false);
  const [scan, setScan] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [applying, setApplying] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [query, setQuery] = useState("");

  const runScan = async () => {
    setLoading(true);
    setSelected(new Set());
    setLastResult(null);
    try {
      const res = await axios.get(`${API}/academy/admin/fee-reconciliation`, { withCredentials: true });
      setScan(res.data);
      if ((res.data.items || []).length === 0) {
        toast.success("All registrations are already correctly priced!");
      }
    } catch (err) {
      { const __e = formatAdminError(err, "Scan failed"); toast.error(__e.message, __e.opts); };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runScan();
  }, []);

  const filteredItems = (scan?.items || []).filter((it) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (it.parent_email || "").toLowerCase().includes(q) ||
      (it.parent_name || "").toLowerCase().includes(q) ||
      (it.term_name || "").toLowerCase().includes(q) ||
      (it.registration_id || "").toLowerCase().includes(q)
    );
  });

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === filteredItems.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredItems.map((i) => i.registration_id)));
    }
  };

  const applySelected = async () => {
    if (selected.size === 0) {
      toast.error("Select at least one registration");
      return;
    }
    const ids = Array.from(selected);
    const confirmMsg = `Apply recalculated fees to ${ids.length} registration${ids.length === 1 ? "" : "s"}? This will also update any pending PayNow QR amounts for these parents.`;
    if (!window.confirm(confirmMsg)) return;

    setApplying(true);
    try {
      const res = await axios.post(
        `${API}/academy/admin/fee-reconciliation/apply`,
        { registration_ids: ids },
        { withCredentials: true }
      );
      setLastResult(res.data);
      toast.success(
        `Applied to ${res.data.succeeded}/${res.data.requested} registrations`
          + (res.data.failed ? ` · ${res.data.failed} failed` : "")
      );
      // Re-scan to refresh the list
      await runScan();
    } catch (err) {
      { const __e = formatAdminError(err, "Bulk apply failed"); toast.error(__e.message, __e.opts); };
    } finally {
      setApplying(false);
    }
  };

  const totalDelta = filteredItems
    .filter((i) => selected.has(i.registration_id))
    .reduce((sum, i) => sum + (i.delta || 0), 0);

  // CSV export — full fee breakdown for every flagged registration so admin
  // can audit what would change before clicking Recalculate.
  const exportCsv = () => {
    if (!filteredItems.length) return;
    const csvEscape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const num = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n.toFixed(2) : "";
    };
    const headers = [
      "registration_id", "parent_name", "parent_email", "term_name", "sibling_count",
      "stored_is_scc_member", "stored_member_type",
      "current_is_scc_member", "current_member_type", "current_is_digital", "current_is_section",
      "membership_number",
      "old_total", "new_total", "delta",
      "old_base_fee", "new_base_fee",
      "old_registration_fee", "new_registration_fee",
      "old_sibling_discount", "new_sibling_discount",
      "old_member_discount", "new_member_discount",
      "old_dm_price", "new_dm_price",
      "credits_applied",
      "total_invoiced", "total_paid", "diff_invoiced_vs_paid",
    ];
    const lines = [headers.map(csvEscape).join(",")];
    for (const it of filteredItems) {
      const oldFb = it.old_breakdown || {};
      const newFb = it.new_breakdown || {};
      const stored = it.registration_stored_status || {};
      const current = it.parent_current_status || {};
      lines.push([
        it.registration_id,
        it.parent_name || "",
        it.parent_email || "",
        it.term_name || "",
        it.sibling_count ?? "",
        stored.is_scc_member ? "Y" : "N",
        stored.member_type || "",
        current.is_scc_member ? "Y" : "N",
        current.member_type || "",
        current.is_digital_member ? "Y" : "N",
        current.is_section_member ? "Y" : "N",
        current.membership_number || "",
        num(it.old_total),
        num(it.new_total),
        num(it.delta),
        num(oldFb.base_fee),
        num(newFb.base_fee),
        num(oldFb.registration_fee),
        num(newFb.registration_fee),
        num(oldFb.sibling_discount),
        num(newFb.sibling_discount),
        num(oldFb.member_discount_amount || oldFb.membership_discount_amount),
        num(newFb.member_discount_amount || newFb.membership_discount_amount),
        num(oldFb.dm_price),
        num(newFb.dm_price),
        num(it.credits_applied),
        num(it.total_invoiced ?? it.old_total),
        num(it.total_paid),
        num(it.diff_invoiced_vs_paid),
      ].map(csvEscape).join(","));
    }
    const csv = lines.join("\n");
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fee-reconciliation-${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredItems.length} row${filteredItems.length === 1 ? "" : "s"}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-950 to-black text-slate-100 p-6 md:p-10">
      <div className="max-w-6xl mx-auto">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6" data-testid="back-to-admin">
          <ArrowLeft className="w-4 h-4" /> Back to Admin Panel
        </Link>

        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl font-bold text-white flex items-center gap-3">
              <RefreshCcw className="w-9 h-9 text-amber-400" />
              Fee Reconciliation
            </h1>
            <p className="text-slate-400 mt-2 max-w-2xl">
              Scans all unpaid registrations and flags ones where the parent's current member status
              differs from what was stored at registration time. Apply in bulk so parents with late-verified
              memberships get their corrected invoice automatically.
            </p>
          </div>
          <Button onClick={runScan} disabled={loading} variant="outline" className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10" data-testid="rescan-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
            Re-scan
          </Button>
        </div>

        {/* Stats */}
        {scan && !loading && (
          <div className="grid md:grid-cols-4 gap-3 mb-6">
            <Card className="p-4 bg-slate-900/60 border-slate-700">
              <p className="text-xs text-slate-400 uppercase">Scanned</p>
              <p className="text-2xl font-bold text-white">{scan.scanned}</p>
              <p className="text-xs text-slate-500 mt-1">unpaid registrations</p>
            </Card>
            <Card className={`p-4 border ${scan.flagged_count > 0 ? "bg-amber-500/10 border-amber-500/40" : "bg-emerald-500/10 border-emerald-500/40"}`}>
              <p className="text-xs uppercase text-slate-400">Needs correction</p>
              <p className={`text-2xl font-bold ${scan.flagged_count > 0 ? "text-amber-300" : "text-emerald-300"}`} data-testid="flagged-count">
                {scan.flagged_count}
              </p>
              <p className="text-xs text-slate-500 mt-1">{scan.flagged_count === 0 ? "all good!" : "mis-priced"}</p>
            </Card>
            <Card className="p-4 bg-slate-900/60 border-slate-700">
              <p className="text-xs text-slate-400 uppercase">Selected</p>
              <p className="text-2xl font-bold text-blue-300" data-testid="selected-count">{selected.size}</p>
            </Card>
            <Card className={`p-4 border ${totalDelta < 0 ? "bg-emerald-500/10 border-emerald-500/40" : totalDelta > 0 ? "bg-amber-500/10 border-amber-500/40" : "bg-slate-900/60 border-slate-700"}`}>
              <p className="text-xs text-slate-400 uppercase">Selected delta</p>
              <p className={`text-2xl font-bold ${totalDelta < 0 ? "text-emerald-300" : totalDelta > 0 ? "text-amber-300" : "text-slate-300"}`}>
                {totalDelta < 0 ? "−" : totalDelta > 0 ? "+" : ""}${Math.abs(totalDelta).toFixed(2)}
              </p>
              <p className="text-xs text-slate-500 mt-1">{totalDelta < 0 ? "net reduction" : totalDelta > 0 ? "net increase" : "no change"}</p>
            </Card>
          </div>
        )}

        {/* Last result banner */}
        {lastResult && (
          <Card className="p-4 bg-emerald-500/10 border-emerald-500/40 mb-6" data-testid="apply-result-banner">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="text-emerald-200 font-semibold">
                  Applied recalculation to {lastResult.succeeded} of {lastResult.requested} registrations
                  {lastResult.failed > 0 && ` (${lastResult.failed} failed)`}
                </p>
                <div className="mt-1 text-xs text-emerald-300/80 flex flex-wrap gap-4">
                  {lastResult.total_reduction < 0 && (
                    <span>Total reduction: <strong>${Math.abs(lastResult.total_reduction).toFixed(2)}</strong></span>
                  )}
                  {lastResult.total_increase > 0 && (
                    <span>Total increase: <strong>${lastResult.total_increase.toFixed(2)}</strong></span>
                  )}
                  {lastResult.transactions_synced > 0 && (
                    <span>{lastResult.transactions_synced} pending payment QR(s) synced</span>
                  )}
                </div>
              </div>
            </div>
          </Card>
        )}

        {loading && (
          <div className="py-16 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-amber-400 mx-auto" />
            <p className="text-slate-400 mt-3">Scanning registrations…</p>
          </div>
        )}

        {!loading && scan && scan.items.length === 0 && (
          <Card className="p-12 bg-emerald-500/5 border-emerald-500/30 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <h3 className="text-xl font-bold text-white">All clear!</h3>
            <p className="text-slate-400 mt-2">No registrations need fee corrections right now.</p>
          </Card>
        )}

        {!loading && scan && scan.items.length > 0 && (
          <>
            {/* Search + bulk controls */}
            <div className="flex gap-2 items-center mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filter by parent email, name, or term…"
                  className="pl-9 bg-slate-900/60 border-slate-700 text-white"
                  data-testid="filter-input"
                />
              </div>
              <Button
                onClick={toggleAll}
                variant="outline"
                className="border-slate-600 text-slate-300"
                data-testid="toggle-all-btn"
              >
                {selected.size === filteredItems.length && filteredItems.length > 0 ? "Clear all" : "Select all visible"}
              </Button>
              <Button
                onClick={exportCsv}
                variant="outline"
                disabled={!filteredItems.length}
                className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10"
                data-testid="export-csv-btn"
                title="Export filtered rows with full fee breakdown"
              >
                <Download className="w-4 h-4 mr-2" /> CSV
              </Button>
              <Button
                onClick={applySelected}
                disabled={applying || selected.size === 0}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="bulk-apply-btn"
              >
                {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Recalculate {selected.size || ""} selected
              </Button>
            </div>

            {/* Table */}
            <Card className="bg-slate-900/40 border-slate-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900/80 border-b border-slate-800">
                    <tr className="text-left text-xs uppercase text-slate-400">
                      <th className="w-10 p-3"></th>
                      <th className="p-3">Parent</th>
                      <th className="p-3">Term</th>
                      <th className="p-3">Stored status</th>
                      <th className="p-3">Current status</th>
                      <th className="p-3 text-right">Old total</th>
                      <th className="p-3 text-right">New total</th>
                      <th className="p-3 text-right">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((it) => {
                      const isChecked = selected.has(it.registration_id);
                      return (
                        <tr
                          key={it.registration_id}
                          className={`border-b border-slate-800/50 hover:bg-slate-800/30 cursor-pointer ${isChecked ? "bg-amber-500/5" : ""}`}
                          onClick={() => toggle(it.registration_id)}
                          data-testid={`recon-row-${it.registration_id}`}
                        >
                          <td className="p-3" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={isChecked}
                              onCheckedChange={() => toggle(it.registration_id)}
                              data-testid={`recon-check-${it.registration_id}`}
                            />
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <User className="w-3 h-3 text-slate-500" />
                              <div>
                                <p className="text-white font-medium">{it.parent_name || <span className="text-slate-500 italic">(no name)</span>}</p>
                                <p className="text-xs text-slate-400">{it.parent_email || "—"}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1 text-slate-300">
                              <Calendar className="w-3 h-3 text-slate-500" />
                              <span className="text-xs">{it.term_name || "—"}</span>
                            </div>
                            {it.sibling_count > 1 && (
                              <Badge variant="outline" className="text-xs border-blue-500/40 text-blue-300 mt-1">
                                {it.sibling_count} kids
                              </Badge>
                            )}
                          </td>
                          <td className="p-3">
                            <Badge className="bg-slate-700/50 text-slate-300 text-xs">
                              {it.registration_stored_status?.is_scc_member ? "SCC member" : "Non-member"}
                            </Badge>
                            <p className="text-xs text-slate-500 mt-1">
                              {it.registration_stored_status?.member_type || "—"}
                            </p>
                          </td>
                          <td className="p-3">
                            <Badge className="bg-emerald-500/20 text-emerald-300 text-xs">
                              {it.parent_current_status?.is_scc_member ? "SCC member" : "Non-member"}
                              {it.parent_current_status?.is_digital_member && " + Digital"}
                              {it.parent_current_status?.is_section_member && " (Section)"}
                            </Badge>
                            <p className="text-xs text-slate-500 mt-1">
                              {it.parent_current_status?.member_type || "—"}
                              {it.parent_current_status?.membership_number ? ` · ${it.parent_current_status.membership_number}` : ""}
                            </p>
                          </td>
                          <td className="p-3 text-right font-mono text-slate-400">
                            <Money v={it.old_total} />
                          </td>
                          <td className="p-3 text-right font-mono text-white font-semibold">
                            <Money v={it.new_total} />
                          </td>
                          <td className={`p-3 text-right font-mono font-semibold ${it.delta < 0 ? "text-emerald-300" : it.delta > 0 ? "text-amber-300" : "text-slate-400"}`}>
                            <span className="inline-flex items-center gap-1">
                              {it.delta < 0 ? <TrendingDown className="w-3 h-3" /> : it.delta > 0 ? <TrendingUp className="w-3 h-3" /> : null}
                              {it.delta < 0 ? "−" : it.delta > 0 ? "+" : ""}${Math.abs(it.delta).toFixed(2)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>

            {filteredItems.length === 0 && query && (
              <p className="text-center text-slate-500 mt-4">No registrations match "{query}"</p>
            )}
          </>
        )}

        {scan?.error_count > 0 && (
          <Card className="p-4 bg-red-500/10 border-red-500/40 mt-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-red-300 font-semibold">{scan.error_count} registration(s) couldn't be scanned</p>
                <p className="text-xs text-red-400/80 mt-1">
                  These are usually missing terms or parent records. Investigate manually:
                </p>
                <ul className="mt-2 text-xs text-red-300/70 space-y-0.5 ml-4">
                  {(scan.errors || []).map((e, i) => (
                    <li key={i}>· <code>{e.registration_id?.slice(0, 8)}…</code> — {e.error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}